import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
}

export const SEO = ({ 
  title, 
  description, 
  keywords,
  ogImage,
  canonical 
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://yourdomain.com';
  
  const defaultTitle = 'LinkedIn Content AI - AI-Powered LinkedIn Content Strategy & Post Generator';
  const defaultDescription = 'Transform your LinkedIn presence with AI-powered content strategy, post generation, and engagement management. Create compelling content 10x faster while maintaining your unique voice.';
  const defaultKeywords = 'LinkedIn AI, LinkedIn content generator, AI post generator, LinkedIn strategy, content marketing, social media AI';
  const defaultOgImage = `${baseUrl}/og-image.png`;
  
  const fullTitle = title ? `${title} | LinkedIn Content AI` : defaultTitle;
  const fullDescription = description || defaultDescription;
  const fullKeywords = keywords || defaultKeywords;
  const fullOgImage = ogImage || defaultOgImage;
  const fullCanonical = canonical || `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', fullDescription);
    updateMetaTag('keywords', fullKeywords);

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', fullDescription, true);
    updateMetaTag('og:image', fullOgImage, true);
    updateMetaTag('og:url', fullCanonical, true);

    // Twitter tags
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', fullDescription);
    updateMetaTag('twitter:image', fullOgImage);

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullCanonical);
  }, [fullTitle, fullDescription, fullKeywords, fullOgImage, fullCanonical]);

  return null;
};
